import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAsync } from 'react-use';
import {
  Header,
  Page,
  Content,
} from '@backstage/core-components';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Typography,
  Box,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Paper,
  Button,
  Tooltip,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { TabPanel } from '../../components/tabpanel/TabPanel';
import { WorkspaceSystem } from './WorkspaceSystems';
import { WorkspaceHistory } from './WorkspaceHistory';
import { WorkspaceEdit } from './WorkspaceEdit';
import { useNotification } from '../../hooks/useNotification';
import { NotificationSnackbar } from '../../components/notification-snackbar/notification-snackbar';
import { WorkspacesService } from '../../services/WorkspacesService';
import { discoveryApiRef, fetchApiRef, microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { openControlWritePermission, openControlAdminPermission } from '@internal/backstage-plugin-supply-features-common';
import { SetMigrationPeriod } from './SetMigrationPeriod';
import { Environment, MigrationPeriod, WorkspaceStatus } from './types';

const ErrorDisplay: React.FC<{
  error: any;
  onRetry: () => void;
  message?: string
}> = ({ error, onRetry, message = 'Error' }) => {
  const errorMessage = error && typeof error === 'object' && error.message
    ? error.message
    : String(error || 'Unknown error');

  return (
    <Paper style={{ padding: 16, backgroundColor: '#ffebee' }}>
      <Typography variant="body2" color="error" style={{ marginBottom: 8 }}>
        {message}: {errorMessage}
      </Typography>
      <Button
        color="primary"
        size="small"
        variant="outlined"
        onClick={onRetry}
      >
        Retry
      </Button>
    </Paper>
  );
};

const formatPeriod = (period?: string) => {
  return period?.trim()
    .replaceAll('-', '/')
    .replaceAll(',', ' - ')
    .replaceAll('[', '').replaceAll(']', '');
}

export const WorkspaceDetailPage = () => {
  const [tabValue, setTabValue] = useState('system');
  const [retryTrigger, setRetryTrigger] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isIdpMigrationModalOpen, setIsIdpMigrationModalOpen] = useState(false);
  const [refreshedFields, setRefreshedFields] = useState<{
    subdomain?: string;
    racId?: string;
    fluigId?: string;
    fluigCompanyDomain?: string;
  } | undefined>(undefined);
  const { notification, hideNotification, showSuccess, showError } = useNotification();
  const { allowed: canEdit } = usePermission({ permission: openControlWritePermission });
  const { allowed: isAdmin } = usePermission({ permission: openControlAdminPermission });

  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  const catalogApi = useApi(catalogApiRef);

  const { environment: envParam, workspaceId } = useParams<{ environment: string; workspaceId: string }>();

  const environment: Environment | undefined = useMemo(() => {
    if (!envParam) return undefined;
    if (['development', 'staging', 'production'].includes(envParam)) {
      return envParam as Environment;
    }
    return undefined;
  }, [envParam]);

  const workspacesService = useMemo(
    () => new WorkspacesService(fetchApi, microsoftAuthApi, discoveryApi, catalogApi),
    [fetchApi, microsoftAuthApi, discoveryApi, catalogApi]
  );

  const { value: workspace, loading, error } = useAsync(async () => {
    if (!environment || !workspaceId) return undefined;
    return await workspacesService.getWorkspaceDetails(environment, workspaceId);
  }, [environment, workspaceId, retryTrigger, workspacesService]);

  const effectiveSubdomain = refreshedFields?.subdomain ?? workspace?.subdomain;
  const effectiveRacId = refreshedFields?.racId ?? workspace?.racId;
  const effectiveFluigId = refreshedFields?.fluigId ?? workspace?.fluigId;
  const effectiveFluigCompanyDomain = refreshedFields?.fluigCompanyDomain ?? workspace?.fluigCompanyDomain;

  useEffect(() => {
    if (!environment || !workspaceId || loading || error) return;
    if (effectiveSubdomain) return;

    const fetchData = async () => {
      try {
        const data = await workspacesService.getWorkspaceDetails(environment, workspaceId);
        setRefreshedFields({
          subdomain: data.subdomain,
          racId: data.racId,
          fluigId: data.fluigId,
          fluigCompanyDomain: data.fluigCompanyDomain,
        });
      } catch {
        // silent refresh — ignore errors
      }
    };

    fetchData();
    const intervalId = setInterval(fetchData, 60_000);

    return () => clearInterval(intervalId);
  }, [environment, workspaceId, loading, error, effectiveSubdomain, workspacesService]);

  const handleRetry = () => {
    setRetryTrigger(prev => prev + 1);
  };

  const handleEdit = () => {
    setEditModalOpen(true);
  };

  const handleEditSave = () => {
    setRetryTrigger(prev => prev + 1);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
  };

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: string) => {
    setTabValue(newValue);
  };

  const handleSetMigrationPeriodOpen = () => {
    setIsIdpMigrationModalOpen(true);
  };

  const handleSetMigrationPeriodClose = () => {
    setIsIdpMigrationModalOpen(false);
  }

  const handleSetMigrationPeriodConfirm = (migrationPeriod: MigrationPeriod): void => {
    if (!environment || !workspaceId) return;

    workspacesService.startIdpMigrationPeriod(environment, workspaceId, migrationPeriod)
      .then(() => {
        showSuccess('Migration period updated successfully.');
        if (workspace) {
          workspace.period = `${migrationPeriod.start.toLocaleDateString()}, ${migrationPeriod.end.toLocaleDateString()}`;
        }
        setIsIdpMigrationModalOpen(false);
      })
      .catch((err: Error) => {
        showError(err.message);
      });
  };

  if (envParam && !environment) {
    return (
      <Page themeId="tool">
        <Header title="Workspace Detail" typeLink='/workspaces' />
        <Content>
          <Paper style={{ padding: 16, backgroundColor: '#ffebee' }}>
            <Typography variant="body2" color="error">
              Invalid environment: {envParam}. Valid environments are: development, staging, production
            </Typography>
          </Paper>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header title="Workspace Detail" type="Workspaces" typeLink={`/workspaces/${environment}`} />
      <Content>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title="Account Details" />
                  <CardContent>
                    {loading && (
                      <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
                        <CircularProgress size={24} />
                      </Box>
                    )}
                    {error && (
                      <ErrorDisplay
                        error={error}
                        onRetry={handleRetry}
                        message="Error loading account details"
                      />
                    )}
                    {!loading && !error && (
                      <>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Totvs Code
                          </Typography>
                          <Typography variant="body1">
                            {workspace?.account.totvsCode}
                          </Typography>
                        </Box>
                        <Box mt={2}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Document
                          </Typography>
                          <Typography variant="body1">
                            {workspace?.account.document}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="Workspace Details"
                    action={
                      <Box display="flex" alignItems="center">
                        {canEdit && (
                          <Tooltip title="Edit information">
                            <IconButton onClick={handleEdit}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {effectiveSubdomain && environment && (
                          <Tooltip title="Go to workspace environment">
                            <IconButton
                              component="a"
                              href={`https://${effectiveSubdomain}.supply-access.${environment}.totvs.app`}
                              target="_blank"
                            >
                              <OpenInNewIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    }
                  />
                  <CardContent style={{ wordBreak: 'break-word' }}>
                    {loading && (
                      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress size={24} />
                      </Box>
                    )}
                    {error && (
                      <ErrorDisplay
                        error={error}
                        onRetry={handleRetry}
                        message="Error loading workspace details"
                      />
                    )}
                    {!loading && !error && (
                      <>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Name
                          </Typography>
                          <Typography variant="body1">
                            {workspace?.name || '(Unknown)'}
                          </Typography>
                        </Box>
                        <Box mt={2}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Subdomain
                          </Typography>
                          <Typography variant="body1">
                            {effectiveSubdomain || '(Unknown)'}
                          </Typography>
                        </Box>
                        <Box mt={2}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Email
                          </Typography>
                          <Typography variant="body1">
                            {workspace?.racAdminEmail || '(Unknown)'}
                          </Typography>
                        </Box>
                        <Box mt={2}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Status
                          </Typography>
                          <Typography variant="body1">
                            {workspace?.status || '(Unknown)'}
                          </Typography>
                        </Box>
                        <Box mt={2}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Purpose
                          </Typography>
                          <Typography variant="body1">
                            {workspace?.purpose || '(Unknown)'}
                          </Typography>
                        </Box>
                        <Box mt={2}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Requester email (CS)
                          </Typography>
                          <Typography variant="body1">
                            {workspace?.requesterEmail || '(Unknown)'}
                          </Typography>
                        </Box>
                        <Box mt={3} mb={1}>
                          <hr style={{ border: 'none', borderTop: '1px solid #ddd', width: '100%', margin: 0 }} />
                        </Box>
                        <Box mt={2}>
                          <Typography variant="subtitle2" color="textSecondary">
                            RAC Id
                          </Typography>
                          <Typography variant="body1">
                            {effectiveRacId || '(Unknown)'}
                          </Typography>
                        </Box>
                        <Box mt={2}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Fluig Id
                          </Typography>
                          <Typography variant="body1">
                            {effectiveFluigId || '(Unknown)'}
                          </Typography>
                        </Box>
                        <Box mt={2}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Fluig Domain
                          </Typography>
                          <Typography variant="body1">
                            {effectiveFluigCompanyDomain || '(Unknown)'}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title="Authorization model" />
                  <CardContent>
                    {loading && (
                      <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
                        <CircularProgress size={24} />
                      </Box>
                    )}
                    {error && (
                      <ErrorDisplay
                        error={error}
                        onRetry={handleRetry}
                        message="Error loading authorization model"
                      />
                    )}
                    {!loading && !error && (
                      <>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Current
                          </Typography>
                          <Typography variant="body1">
                            {workspace?.idp || '(Unknown)'}
                          </Typography>
                        </Box>
                        <Box mt={2}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Migration period
                          </Typography>
                          <Typography variant="body1">
                            {formatPeriod(workspace?.period) || '(Not defined)'}
                          </Typography>
                        </Box>
                        {isAdmin && (
                          <>
                            <Box mt={2} mb={1}>
                              <hr style={{ border: 'none', borderTop: '1px solid #ddd', width: '100%', margin: 0 }} />
                            </Box>
                            <Box mt={2}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="medium"
                                onClick={handleSetMigrationPeriodOpen}
                              >
                                Set migration period
                              </Button>
                            </Box>
                          </>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={8}>
            <Box>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label="System" value="system" />
                <Tab label="History" value="history" />
              </Tabs>

              <TabPanel value={tabValue} index="system">
                <WorkspaceSystem isWorkspaceActive={workspace?.status === WorkspaceStatus.ACTIVE} />
              </TabPanel>

              <TabPanel value={tabValue} index="history">
                <WorkspaceHistory />
              </TabPanel>
            </Box>
          </Grid>
        </Grid>

        <WorkspaceEdit
          open={editModalOpen}
          onClose={handleEditClose}
          environment={environment}
          workspaceId={workspaceId}
          workspace={workspace ? {
            name: workspace.name,
            subdomain: workspace.subdomain,
            environment: workspace.purpose,
            requesterEmail: workspace.requesterEmail,
          } : undefined}
          onSave={handleEditSave}
        />

        <SetMigrationPeriod
          open={isIdpMigrationModalOpen}
          onClose={handleSetMigrationPeriodClose}
          onConfirm={handleSetMigrationPeriodConfirm}
        />

        <NotificationSnackbar notification={notification} onClose={hideNotification} />
      </Content>
    </Page>
  );
};
